import type { LayoutConfig } from '../../engine/plant/types';

/**
 * P&ID layout. Positions only — every symbol is drawn by the shared symbol library from
 * the equipment type, and every moving part reflects engine state (CLAUDE.md rules 1, 6).
 */

/** Vertical centre of each filter row on the overview. */
const FILTER_ROWS = [170, 300, 430, 560];

export const layout: LayoutConfig = {
  views: [
    {
      id: 'overview',
      name: 'Plant overview',
      viewBox: [0, 0, 1720, 940],
      nodes: [
        { equipment: 'SRC-001', x: 70, y: 640, label: 'River intake' },
        { equipment: 'P-101A', x: 180, y: 590 },
        { equipment: 'P-101B', x: 180, y: 690 },
        { equipment: 'HD-101', x: 265, y: 640 },
        { equipment: 'PD-201', x: 355, y: 470 },
        { equipment: 'MX-201', x: 360, y: 640 },
        { equipment: 'FL-202', x: 500, y: 640 },
        { equipment: 'CL-301', x: 660, y: 640 },
        { equipment: 'FCH-401', x: 790, y: 520 },
        ...FILTER_ROWS.flatMap((y, i) => [
          { equipment: `IV-40${i + 1}`, x: 880, y },
          { equipment: `F-40${i + 1}`, x: 960, y },
          { equipment: `FV-40${i + 1}`, x: 1050, y },
        ]),
        { equipment: 'PD-601', x: 1180, y: 380 },
        { equipment: 'CT-601', x: 1200, y: 560 },
        { equipment: 'PD-602', x: 1330, y: 380 },
        { equipment: 'CW-701', x: 1340, y: 640 },
        { equipment: 'P-701A', x: 1470, y: 590 },
        { equipment: 'P-701B', x: 1470, y: 690 },
        { equipment: 'HD-701', x: 1553, y: 640 },
        { equipment: 'SNK-DIST', x: 1645, y: 640, label: 'Distribution' },
        { equipment: 'SNK-SLUDGE', x: 660, y: 856, label: 'Sludge lagoon' },
        { equipment: 'SNK-WASTE', x: 1200, y: 856, label: 'Washwater recovery' },
      ],
      pipes: [
        { id: 'p-raw-a', points: [105, 640, 145, 640, 145, 590, 163, 590], flowTag: 'P-101A.Q' },
        { id: 'p-raw-b', points: [105, 640, 145, 640, 145, 690, 163, 690], flowTag: 'P-101B.Q' },
        { id: 'p-hd-a', points: [197, 590, 258, 590, 258, 640], flowTag: 'P-101A.Q' },
        { id: 'p-hd-b', points: [197, 690, 258, 690, 258, 640], flowTag: 'P-101B.Q' },
        { id: 'p-101', points: [272, 640, 328, 640], flowTag: 'S-101.Q', label: 'Raw water' },
        { id: 'p-alum', points: [355, 492, 355, 608], flowTag: 'PD-201.LPH', utility: true, label: 'Alum' },
        { id: 'p-202', points: [388, 640, 460, 640], flowTag: 'S-101.Q' },
        { id: 'p-301', points: [540, 640, 618, 640], flowTag: 'S-101.Q' },
        { id: 'p-sludge', points: [660, 676, 660, 840], flowTag: 'CL-301.BLOWDOWN', label: 'Blowdown' },
        { id: 'p-302', points: [702, 622, 750, 622, 750, 520, 770, 520], flowTag: 'S-302.Q', label: 'Settled water' },
        { id: 'p-fch', points: [822, 520, 838, 520], flowTag: 'S-302.Q' },
        { id: 'p-chan-trunk', points: [838, 170, 838, 560] },
        ...FILTER_ROWS.flatMap((y, i) => [
          { id: `p-in-${i}`, points: [838, y, 862, y], flowTag: `FT-40${i + 1}.PV` },
          { id: `p-box-${i}`, points: [898, y, 930, y], flowTag: `FT-40${i + 1}.PV` },
          { id: `p-out-${i}`, points: [990, y, 1032, y], flowTag: `F-40${i + 1}.QF` },
          { id: `p-coll-${i}`, points: [1068, y, 1110, y], flowTag: `F-40${i + 1}.Q` },
        ]),
        { id: 'p-comb-trunk', points: [1110, 170, 1110, 560] },
        { id: 'p-ct', points: [1110, 560, 1166, 560], flowTag: 'CALC-405.OUT', label: 'Filtered' },
        { id: 'p-waste', points: [1110, 560, 1110, 850, 1174, 850], flowTag: 'F-401.QW', utility: true, label: 'To waste' },
        { id: 'p-cl2', points: [1180, 398, 1180, 534], flowTag: 'PD-601.LPH', utility: true, label: 'Cl₂' },
        { id: 'p-601', points: [1236, 570, 1290, 570, 1290, 640, 1306, 640], flowTag: 'CALC-405.OUT' },
        { id: 'p-naoh', points: [1330, 398, 1330, 600], flowTag: 'PD-602.LPH', utility: true, label: 'NaOH' },
        { id: 'p-bw', points: [1340, 680, 1340, 850, 1228, 850], flowTag: 'F-401.QBW', utility: true, label: 'Backwash supply' },
        { id: 'p-lift-a', points: [1374, 640, 1440, 640, 1440, 590, 1453, 590], flowTag: 'P-701A.Q' },
        { id: 'p-lift-b', points: [1374, 640, 1440, 640, 1440, 690, 1453, 690], flowTag: 'P-701B.Q' },
        { id: 'p-hd7-a', points: [1487, 590, 1546, 590, 1546, 640], flowTag: 'P-701A.Q' },
        { id: 'p-hd7-b', points: [1487, 690, 1546, 690, 1546, 640], flowTag: 'P-701B.Q' },
        { id: 'p-dist', points: [1560, 640, 1619, 640], flowTag: 'S-DIST.Q', label: 'To network' },
      ],
      bubbles: [
        { ref: 'AT-101', x: 70, y: 556, leaderTo: [70, 624] },
        { ref: 'FT-101', x: 240, y: 556, leaderTo: [268, 624] },
        { ref: 'FIC-101', x: 306, y: 736, leaderTo: [278, 658], shared: true },
        { ref: 'AIC-201', x: 442, y: 462, leaderTo: [376, 470], shared: true },
        { ref: 'AT-301', x: 748, y: 700, leaderTo: [700, 656] },
        { ref: 'LT-301', x: 604, y: 762, leaderTo: [650, 678] },
        { ref: 'LSC-301', x: 682, y: 762, leaderTo: [666, 678], shared: true },
        { ref: 'FT-302', x: 744, y: 444, leaderTo: [756, 512] },
        { ref: 'LT-411', x: 818, y: 444, leaderTo: [800, 500] },
        ...FILTER_ROWS.map((y, i) => ({
          ref: `FIC-40${i + 1}`,
          x: 1050,
          y: y - 58,
          leaderTo: [1050, y - 26] as [number, number],
          shared: true,
        })),
        { ref: 'AT-405', x: 1165, y: 120, leaderTo: [1113, 172] },
        { ref: 'AIC-601', x: 1252, y: 372, leaderTo: [1204, 380], shared: true },
        { ref: 'AIC-602', x: 1402, y: 372, leaderTo: [1354, 380], shared: true },
        { ref: 'LT-501', x: 1272, y: 762, leaderTo: [1314, 682] },
        { ref: 'LIC-501', x: 1350, y: 762, leaderTo: [1340, 682], shared: true },
        { ref: 'FT-701', x: 1612, y: 556, leaderTo: [1600, 624] },
        { ref: 'PIC-701', x: 1660, y: 736, leaderTo: [1580, 664], shared: true },
      ],
      labels: [
        { x: 180, y: 512, text: 'INTAKE', size: 13, anchor: 'middle', muted: true },
        { x: 430, y: 512, text: 'COAGULATION & FLOCCULATION', size: 13, anchor: 'middle', muted: true },
        { x: 660, y: 512, text: 'CLARIFICATION', size: 13, anchor: 'middle', muted: true },
        { x: 960, y: 96, text: 'RAPID GRAVITY FILTERS', size: 13, anchor: 'middle', muted: true },
        { x: 1200, y: 468, text: 'DISINFECTION', size: 13, anchor: 'middle', muted: true },
        { x: 1340, y: 512, text: 'CLEARWELL & pH TRIM', size: 13, anchor: 'middle', muted: true },
        { x: 1530, y: 512, text: 'HIGH LIFT', size: 13, anchor: 'middle', muted: true },
      ],
      values: [
        { tag: 'AT-101.PV', x: 30, y: 828, label: 'Raw turbidity', units: 'NTU', decimals: 2, anchor: 'start' },
        { tag: 'TT-101.PV', x: 30, y: 850, label: 'Raw temperature', units: 'K', decimals: 1, anchor: 'start' },
        { tag: 'MX-201.DOSE', x: 300, y: 828, label: 'Alum dose', units: 'mg/L', decimals: 2, anchor: 'start' },
        { tag: 'MX-201.DOSE_OPT', x: 300, y: 850, label: 'Optimum dose', units: 'mg/L', decimals: 2, anchor: 'start' },
        { tag: 'CL-301.SOR', x: 570, y: 828, label: 'Overflow rate', units: 'm/h', decimals: 2, anchor: 'start' },
        { tag: 'AT-301.PV', x: 570, y: 850, label: 'Settled turbidity', units: 'NTU', decimals: 2, anchor: 'start' },
        { tag: 'CALC-406.OUT', x: 1190, y: 120, label: 'Filters in service', units: '', decimals: 0, anchor: 'start' },
        { tag: 'AT-405.PV', x: 1190, y: 142, label: 'Final turbidity', units: 'NTU', decimals: 3, anchor: 'start' },
        { tag: 'CT-601.CTRATIO', x: 1430, y: 806, label: 'CT ratio', units: '', decimals: 2, anchor: 'start' },
        { tag: 'CT-601.CL2', x: 1430, y: 828, label: 'Free chlorine', units: 'mg/L', decimals: 2, anchor: 'start' },
        { tag: 'AT-602.PV', x: 1430, y: 850, label: 'Final pH', units: '', decimals: 2, anchor: 'start' },
      ],
    },
    {
      id: 'filters',
      name: 'Filter detail',
      viewBox: [0, 0, 1000, 800],
      nodes: [
        { equipment: 'FCH-401', x: 110, y: 400 },
        ...[0, 1, 2, 3].flatMap((i) => {
          const n = i + 1;
          const y = 140 + i * 170;
          return [
            { equipment: `IV-40${n}`, x: 250, y },
            { equipment: `F-40${n}`, x: 420, y, scale: 1.35 },
            { equipment: `FV-40${n}`, x: 610, y },
          ];
        }),
        { equipment: 'CT-601', x: 800, y: 400 },
      ],
      pipes: [
        { id: 'fch-trunk', points: [180, 400, 210, 400, 210, 140, 210, 650] },
        ...[0, 1, 2, 3].map((i) => ({
          id: `fd-in-${i}`,
          points: [210, 140 + i * 170, 235, 140 + i * 170],
          flowTag: `FT-40${i + 1}.PV`,
        })),
        ...[0, 1, 2, 3].map((i) => ({
          id: `fd-box-${i}`,
          points: [265, 140 + i * 170, 380, 140 + i * 170],
          flowTag: `FT-40${i + 1}.PV`,
        })),
        ...[0, 1, 2, 3].map((i) => ({
          id: `fd-out-${i}`,
          points: [460, 140 + i * 170, 595, 140 + i * 170],
          flowTag: `F-40${i + 1}.QF`,
        })),
        ...[0, 1, 2, 3].map((i) => ({
          id: `fd-cw-${i}`,
          points: [625, 140 + i * 170, 720, 140 + i * 170, 720, 400, 775, 400],
          flowTag: `F-40${i + 1}.Q`,
        })),
        ...[0, 1, 2, 3].map((i) => ({
          id: `fd-waste-${i}`,
          points: [625, 140 + i * 170, 660, 140 + i * 170, 660, 100 + i * 170],
          flowTag: `F-40${i + 1}.QW`,
          utility: true,
          label: 'waste',
        })),
      ],
      bubbles: [
        { ref: 'LT-411', x: 110, y: 330, leaderTo: [110, 382] },
        ...[0, 1, 2, 3].flatMap((i) => {
          const n = i + 1;
          const y = 140 + i * 170;
          return [
            { ref: `FIC-40${n}`, x: 610, y: y - 62, leaderTo: [610, y - 16] as [number, number], shared: true },
            { ref: `PDT-40${n}`, x: 420, y: y - 62, leaderTo: [420, y - 22] as [number, number] },
            { ref: `AT-40${n}`, x: 500, y: y + 62, leaderTo: [480, y + 16] as [number, number] },
          ];
        }),
      ],
      labels: [
        { x: 500, y: 40, text: 'RAPID GRAVITY FILTERS — 4 × 25 m², design 5 m/h', size: 14, anchor: 'middle', muted: true },
      ],
      values: [
        ...[0, 1, 2, 3].flatMap((i) => {
          const n = i + 1;
          const y = 140 + i * 170;
          return [
            { tag: `F-40${n}.RUNH`, x: 300, y: y + 55, label: 'Run', units: 'h', decimals: 1, anchor: 'start' as const },
            { tag: `F-40${n}.LOADING`, x: 300, y: y + 75, label: 'Rate', units: 'm/h', decimals: 2, anchor: 'start' as const },
          ];
        }),
      ],
    },
  ],
};
